import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["size", "children", "startIcon", "endIcon", "loading", "disabled", "as", "fluid"];
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import ButtonBase from '../ButtonBase';
import Spinner from '../Spinner';
const Root = styled(ButtonBase).withConfig({
  displayName: "Button__Root",
  componentId: "sc-7cbhwm-0"
})(["", " ", ""], _ref => {
  let {
    $startIcon,
    loading
  } = _ref;
  return ($startIcon || loading) && css(["padding-left:", ";> svg:first-child,> ", ":first-child{margin-right:", ";}"], _ref2 => {
    let {
      size
    } = _ref2;
    return size === ButtonBase.sizes.md ? "var(--size-spacing-03)" : "var(--size-spacing-02)";
  }, Spinner.Style, _ref3 => {
    let {
      size
    } = _ref3;
    return size === ButtonBase.sizes.md ? "var(--size-spacing-02)" : "var(--size-spacing-01)";
  });
}, _ref4 => {
  let {
    $endIcon
  } = _ref4;
  return $endIcon && css(["padding-right:var(--size-spacing-02);> svg:last-child{margin-left:var(--size-spacing-01);}"]);
});
const Button = /*#__PURE__*/forwardRef((_ref5, ref) => {
  let {
      size,
      children,
      startIcon,
      endIcon,
      loading,
      disabled,
      as,
      fluid
    } = _ref5,
    rest = _objectWithoutPropertiesLoose(_ref5, _excluded);
  const renderStartIcon = loading ? /*#__PURE__*/React.createElement(Spinner, {
    size: Spinner.sizes.sm
  }) : startIcon ? /*#__PURE__*/React.cloneElement(startIcon, {
    size
  }) : null;
  const renderEndIcon = endIcon ? /*#__PURE__*/React.cloneElement(endIcon, {
    size
  }) : null;
  return /*#__PURE__*/React.createElement(Root, _extends({
    ref: ref,
    $startIcon: !!startIcon,
    $endIcon: !!endIcon,
    loading: loading,
    fluid: fluid,
    forwardedAs: as,
    size: size,
    disabled: disabled || loading
  }, rest), renderStartIcon, children, renderEndIcon);
});
Button.displayName = 'Button';
Button.sizes = ButtonBase.sizes;
Button.kinds = ButtonBase.kinds;
Button.types = ButtonBase.types;
Button.defaultProps = {
  children: '',
  type: Button.types.button,
  kind: Button.kinds.primary,
  size: Button.sizes.md,
  startIcon: null,
  endIcon: null,
  loading: false,
  fluid: false
};
process.env.NODE_ENV !== "production" ? Button.propTypes = {
  /**
   * Set element type to be rendered for this component. By default it will be render a `<button>` element.
   */
  as: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  /**
   * Sets button children.
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.array, PropTypes.node]),
  /**
   * Sets button type.
   */
  type: PropTypes.oneOf([Button.types.button, Button.types.submit, Button.types.reset]),
  /**
   * Sets button kind for styling.
   */
  kind: PropTypes.oneOf([Button.kinds.primary, Button.kinds.secondary, Button.kinds.tertiary, Button.kinds.neutral, Button.kinds.danger]),
  /**
   * Sets the button size.
   */
  size: PropTypes.oneOf([Button.sizes.sm, Button.sizes.md]),
  /**
   * Sets icon to be displayed at the beginning of the line.
   */
  startIcon: PropTypes.node,
  /**
   * Sets icon to be displayed at the end of the line.
   */
  endIcon: PropTypes.node,
  /**
   * Sets loading appearance. When loading is `true`, then disabled is `true`.
   */
  loading: PropTypes.bool,
  /**
   * Makes Button full-width
   */
  fluid: PropTypes.bool
} : void 0;
export default Button;